import { Col, Row } from 'antd';
import { Box, LoadingIndicator, QueryErrorAlert } from 'components';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useMemo } from 'react';

import { useDashboardOverviewQuery } from '../../queries';
import DashboardLineChart from './DashboardLineChart';
import DashboardRangeControls from './DashboardRangeControls';
import StatCard from './StatCard';
import type { DashboardRange } from './useDashboardRange';

interface OverviewTabProps {
  range: DashboardRange;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ range }) => {
  const { tDashboard } = useCustomTranslation();
  const { from, to, granularity } = range;
  const overviewQuery = useDashboardOverviewQuery(from, to, granularity);
  const data = overviewQuery.data;

  // KPI cards show window-level totals (correct distinct for active companies/users; sums for the
  // rest; headcount at window end). Point-summing would over-count the distinct metrics.
  const cards = useMemo(() => {
    if (!data) return [];
    const { totals } = data;
    return [
      {
        title: tDashboard('overview_activeCompanies'),
        value: totals.activeCompanies
      },
      {
        title: tDashboard('overview_activeUsers'),
        value: totals.activeUsers
      },
      {
        title: tDashboard('overview_userCount'),
        value: totals.userCount
      },
      {
        title: tDashboard('overview_plannedInterventions'),
        value: totals.plannedInterventions
      },
      {
        title: tDashboard('overview_submittedForms'),
        value: totals.submittedForms
      }
    ];
  }, [tDashboard, data]);

  // Plotted as-is. An earlier version smoothed these with a moving average, which turned counts into
  // decimals ("44.3 aktivních firem") and put the chart at odds with the KPI cards above it.
  const lineData = useMemo(() => {
    if (!data) return [];
    const { points } = data;
    return [
      {
        id: tDashboard('overview_activeCompanies'),
        data: points.map((p) => ({ x: p.period, y: p.activeCompanies }))
      },
      {
        id: tDashboard('overview_userCount'),
        data: points.map((p) => ({ x: p.period, y: p.userCount }))
      },
      {
        id: tDashboard('overview_activeUsers'),
        data: points.map((p) => ({ x: p.period, y: p.activeUsers }))
      }
    ];
  }, [tDashboard, data]);

  return (
    <>
      <DashboardRangeControls {...range} />

      {overviewQuery.error ? (
        <QueryErrorAlert error={overviewQuery.error} onRetry={overviewQuery.refetch} />
      ) : overviewQuery.isFetching || !data ? (
        <LoadingIndicator />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {cards.map((card) => (
              <Col xs={12} md={12} lg={6} key={card.title}>
                <StatCard title={card.title} value={card.value} />
              </Col>
            ))}
          </Row>

          <div style={{ marginTop: 16 }}>
            <Box>
              <h4>{tDashboard('overview_activeOverTime')}</h4>
              <p>{tDashboard('overview_definitions_hint')}</p>
              <DashboardLineChart data={lineData} granularity={granularity} />
            </Box>
          </div>
        </>
      )}
    </>
  );
};

export default OverviewTab;
